import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, Copy, Trash2, RefreshCw, ExternalLink, Youtube, Scissors } from "lucide-react";
import { PublishDialog } from "@/components/publish/PublishDialog";
import { PubStats } from "@/components/publish/PubStats";
import { YT_ON_HOLD, YtBuildBadge } from "@/components/publish/YtBuild";
import { mediaUrl, fmtTime, platformLabel, PLATFORMS } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { InlineLoader } from "@/components/LoadingScreen";
import { ShareToggle } from "@/components/ShareToggle";


export const ClipCard = ({ c: clip, onDelete, onRerender, index = 0, selected = false, onSelect }) => {
  const { t } = useI18n();
  const nav = useNavigate();
  const [c, setC] = useState(clip);
  const [pubOpen, setPubOpen] = useState(false);
  useEffect(() => { setC(clip); }, [clip]);
  const text = [c.title, c.description, (c.hashtags || []).join(" ")].filter(Boolean).join("\n\n");
  const copy = async () => { await navigator.clipboard.writeText(text); toast.success(t("cl.copied")); };
  const publish = async () => {
    await navigator.clipboard.writeText(text).catch(() => {});
    window.open(PLATFORMS.find((p) => p.id === c.platform)?.upload || "https://www.tiktok.com/upload", "_blank");
    toast.success(t("cl.publish_done"));
  };
  return (
    <div data-testid="clip-card" data-selected={selected ? "true" : "false"} className={`cx-card cx-card-hover cx-rise rounded-xl overflow-hidden flex flex-col ${selected ? "ring-2 ring-[#ff4fd8] shadow-[0_0_24px_-6px_rgba(255,79,216,0.8)]" : ""}`} style={{ animationDelay: `${index * 0.04}s` }}>
      <div className="bg-black grid place-items-center h-[300px] sm:h-[360px] relative overflow-hidden">
        {onSelect && (
          <label className="absolute top-2.5 right-2.5 z-10 cursor-pointer rounded-md bg-black/70 border border-white/20 p-1.5 flex items-center hover:border-[#ff4fd8]/70" data-testid="clip-select-label">
            <input type="checkbox" checked={selected} onChange={() => onSelect(c.id)} data-testid="clip-select-checkbox" aria-label={t("cl.select")} className="w-4 h-4 accent-[#ff4fd8] cursor-pointer" />
          </label>
        )}
        {c.status === "done" ? (
          <video src={mediaUrl(`/clips/${c.id}/file`)} controls preload="metadata" playsInline className="w-full h-full object-contain" data-testid="clip-video" />
        ) : c.status === "rendering" ? (
          <div className="text-center cx-scanbar w-full h-full grid place-items-center" data-testid="clip-rendering">
            <InlineLoader label={t("cl.rendering")} />
          </div>
        ) : (
          <div className="text-center p-6" data-testid="clip-error">
            <div className="text-rose-400 text-sm">{t("cl.failed")}</div>
            <div className="text-xs text-slate-500 mt-2 line-clamp-3">{c.error}</div>
          </div>
        )}
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          <span className="text-[10px] font-mono bg-black/70 border border-white/10 px-2 py-1 rounded">{c.aspect}</span>
          <span className="text-[10px] font-mono bg-[#2f7bff]/30 border border-[#2f7bff]/50 px-2 py-1 rounded">{platformLabel(c.platform)}</span>
          {c.score != null && <span className="text-[10px] font-mono bg-[#a746ff]/30 border border-[#a746ff]/50 px-2 py-1 rounded">{c.score}</span>}
        </div>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="font-display font-semibold text-white line-clamp-2" data-testid="clip-title">{c.title}</div>
        <div className="text-xs text-slate-500 mt-1 truncate">{c.video_title} · {fmtTime(c.start)}–{fmtTime(c.end)} · {(c.end - c.start).toFixed(0)}s</div>
        {c.hashtags?.length > 0 && <div className="text-xs font-mono text-[#3ad7ff] mt-2 line-clamp-2">{c.hashtags.join(" ")}</div>}
        {c.effects?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2" data-testid="clip-effects">
            {c.effects.map((f) => <span key={f} className="text-[10px] px-1.5 py-0.5 rounded bg-[#a746ff]/15 text-[#c79bff] border border-[#a746ff]/30">{t(f === "hook" ? "fx.hook_fx" : `fx.${f}`)}</span>)}
          </div>
        )}
        <ShareToggle c={c} />
        <PubStats clip={c} onClip={setC} />
        <div className="mt-auto pt-4 space-y-2">
        {c.status === "done" && YT_ON_HOLD && <YtBuildBadge testid="clip-yt-build-badge" />}
        <div className="flex gap-2">
          {c.status === "done" && (
            <a data-testid="clip-download-btn" href={mediaUrl(`/clips/${c.id}/file`, "&download=1")} className="cx-btn flex-1 rounded-full py-2 text-xs font-semibold flex items-center justify-center gap-1.5"><Download className="w-3.5 h-3.5" /> {t("cl.download")}</a>
          )}
          {c.status === "done" && c.video_id && (
            <button data-testid="clip-edit-btn" onClick={() => nav(`/editor/${c.video_id}?clip=${c.id}`)} title={t("cl.edit_hint")} className="cx-ghost rounded-full px-3 py-2 text-xs font-semibold flex items-center gap-1.5 hover:text-[#3ad7ff]"><Scissors className="w-3.5 h-3.5" /> {t("cl.edit")}</button>
          )}
          {c.status === "error" && (
            <button data-testid="clip-rerender-btn" onClick={() => onRerender(c)} className="cx-btn flex-1 rounded-full py-2 text-xs font-semibold flex items-center justify-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> {t("cl.retry")}</button>
          )}
          {c.status === "rendering" && <div className="flex-1" />}
          {c.status === "done" && (YT_ON_HOLD
            ? <button data-testid="clip-yt-publish-btn" disabled title={t("pub.build_hint")} className="cx-ghost rounded-full p-2 opacity-40 cursor-not-allowed"><Youtube className="w-4 h-4" /></button>
            : <button data-testid="clip-yt-publish-btn" onClick={() => setPubOpen(true)} title={t("pub.btn")} className="cx-ghost rounded-full p-2 hover:text-[#ff0033]"><Youtube className="w-4 h-4" /></button>)}
          <button data-testid="clip-copy-hashtags-btn" onClick={copy} title={t("cl.copy")} className="cx-ghost rounded-full p-2"><Copy className="w-4 h-4" /></button>
          <button data-testid="clip-publish-btn" onClick={publish} title={t("cl.publish")} className="cx-ghost rounded-full p-2"><ExternalLink className="w-4 h-4" /></button>
          <button data-testid="clip-delete-btn" onClick={() => onDelete(c)} title={t("cl.delete")} className="cx-ghost rounded-full p-2 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
        </div>
        </div>
      </div>
      <PublishDialog clip={c} open={pubOpen} onOpenChange={setPubOpen} onPublished={(p) => setC((x) => ({ ...x, publications: [...(x.publications || []), p] }))} />
    </div>
  );
};
